import { Inject } from '@odinjs/odin';
import { ClickEvent, FetchEvent, MountEvent, RenderEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import Controller, { OnAfterFetch, OnClick, OnDynamicContentRender, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { HistoryParameters, HttpService, MessageService, } from '@philips-internal/oswald-ui-core/lib/services';
import ReportService from '../../services/report-service';
import BaseController from '../BaseController';
import { Data } from '../../interfaces/data';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import React from 'react';

import './exam-lab-details-controller.css';
import { ActionAPI, DeckAPI, DeckValue, DropDownMenuAPI, FetchAPI, FieldListAPI, ModalService, PanelAPI, PDFViewerAPI, RepeaterAPI, TabControlAPI, TabWithContentAPI, TextAreaAPI } from '@philips-internal/oswald-ui-components';
import { CardExamsEstablishmentInformationApi, CardExamsInformationAPI } from '@philips-emr/patient-components/src';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import UserContext from '../../utils/user-context';
import { ApplicationModulesIds } from '../../app/module-registry/application-modules';
import { Value } from '@philips-internal/oswald-ui-core';
import { ExamLabDetailsAliases } from '../../codegen';

interface ExamLabDetail {
  blockResult: string;
  cardOnSidebar: boolean;
  cdEstabelecimento: string;
  dtAprovacao?: string;
  dtProcedimento: string;
  ieReview: string;
  internSequenceNumber: string;
  nmExame: string;
  nmPessoaFisica: string;
  nrPrescricao: string;
  nrSeqExame: string;
  nrSequencia: string;
  nrStatusAvailable: number;
  nrStatusProcessing: number;
  nrStatusReady: number;
  review?: boolean;
  status: string;
  type: string;
}

enum STATUS_RESULT_EXAM {
  AVAILABLE = 'A',
  BLOCK = 'S',
  READY = 'R'
}

enum EXAMS_TYPE {
  LABORATORY = 'L',
  NON_LABORATORY = 'N'
}

enum VALUE_PARAMS {
  STATUS_EXAME = 35
}

interface CurrentRouteParams extends HistoryParameters {
  filterExamStatus: string;
  filterExamType: string;
  filterFromDate: string;
}

@Controller({ component: 'layout-exam-lab-details', fragment: 'exam-lab-details' })
class ExamLabDetails extends BaseController {

  @Inject('fetch-exam-lab-details')
    fetchExamDetails!: FetchAPI

  // Manual Inject
  pdfViewer!: PDFViewerAPI

  @Inject(ExamLabDetailsAliases.CARD_EXAMS_ESTABLISHMENT_INFORMATION_RIGHT_CARD_EXAM_LAB_DETAILS)
    cardEstablishment!: CardExamsEstablishmentInformationApi;

  @Inject('fetch-exams')
    fetchExamsApi!: FetchAPI;

  @Inject('tab-with-content-available')
    tabAvailable!: TabWithContentAPI;

  @Inject('tab-with-content-processing')
    tabProcessing!: TabWithContentAPI;

  @Inject('panel-exam-card')
    panelDetails!: PanelAPI;

  @Inject('download-exam-btn')
    downloadButton!: ActionAPI;

  @Inject('panel-mail')
    panelMailBtn!: PanelAPI;

  @Inject('panel-loader-establishment')
    panelEstablishmentLoader!: PanelAPI;

  @Inject(ExamLabDetailsAliases.PANEL_EXAM_LAB_TITLE_LOADER_EXAM_LAB_DETAILS)
    panelDetailsLoaderOne!: PanelAPI;

  @Inject(ExamLabDetailsAliases.PANEL_LEFT_CARD_LOADER_EXAM_LAB_DETAILS)
    panelDetailsLoaderTwo!: PanelAPI;

  @Inject('panel-content-dropdown-send-mail')
    panelContentDropdownSendMail!: PanelAPI;

  @Inject('panel-loader-send-mail')
    panelLoaderSendMail!: PanelAPI;

  @Inject('button-email')
    emailButton!: ActionAPI;

  @Inject('field-list-dropdown-send-mail')
    fieldListDropdownSendMail!: FieldListAPI;

  @Inject('drop-down-menu-send-mail')
    dropDownMenuSendMail!: DropDownMenuAPI;

  @Inject('send-mail-field-notes')
    sendMailFieldNotes!: TextAreaAPI;

  @Inject('panel-message')
    panelReportError!: PanelAPI;

  @Inject('panel-exam-title')
    panelExamTitle!: PanelAPI;

  @Inject('panel-exam-left-card')
    panelExamInfo!: PanelAPI;

  @Inject('panel-exam-right-card')
    panelEstablishmentInfo!: PanelAPI;

  @Inject('panel-loader-report')
    panelReportLoader!: PanelAPI;

  @Inject(ExamLabDetailsAliases.DECK_EXAMS_AVAILABLE)
    deckExamsAvailableAndReadyAPI!: DeckAPI;

  @Inject('card-exams-information')
    cardInformationApi!: CardExamsInformationAPI;

  @Inject('repeater-exams-processing')
    repeaterExamsProcessingApi!: RepeaterAPI;

  @Inject('tab-control')
    tabControlAPI!: TabControlAPI;

  @OnMount(ExamLabDetailsAliases.DECK_EXAMS_AVAILABLE)
  handleDeckExamsAvailableLoading(): void {
    const availableResponseBody = this.context.get('availableResponseBody') as [];
    const readyResponseBody = this.context.get('readyResponseBody') as [];
    if (availableResponseBody || readyResponseBody) {
      this.deckExamsAvailableAndReadyAPI.setData([...readyResponseBody, ...availableResponseBody] as Data[]);
    } else {
      this.deckExamsAvailableAndReadyAPI.clearPersistedState();
    }
  }

  @OnMount(ExamLabDetailsAliases.PANEL_LOADER_REPORT)
  handleReport(): void {
    const availableResponseBody = this.context.get('availableResponseBody') as [];
    if (availableResponseBody) {
      this.loadReport(availableResponseBody);
    }
  }

  @OnMount('pdf-viewer-exam-details')
  handlePdfViewerLoading(event: MountEvent): void {
    this.pdfViewer = event.target as PDFViewerAPI;
  }

  @OnMount({target: 'tab-with-content-available'})
  handleTabWithContentAvailableLoading(): void {
    if (this.deckExamsAvailableAndReadyAPI) {
      this.deckExamsAvailableAndReadyAPI.restoreState();
      const tabCount = this.deckExamsAvailableAndReadyAPI.getData().length || 0
      this.tabAvailable.setBadge(tabCount);
    }

    if (this.pdfViewer) {
      this.pdfViewer.restoreState();
    }
  }

  @OnMount({ target: 'tab-with-content-processing'})
  handleTabWithContentProcessing(): void {
    const processingResponseBody = this.context.get('processResponseBody') as [];
    if (processingResponseBody) {
      const countBadgeProcessing = processingResponseBody?.length;
      this.tabProcessing.setBadge(countBadgeProcessing);
    }
  }

  @OnMount({ target: 'layout-exam-lab-details'})
  onLayoutExamsDetail(): void {
    const event = new CustomEvent('updateModulesRoute', { detail: { moduleId: ApplicationModulesIds.EXAMS } });
    dispatchEvent(event);
  }

  @OnUnmount(ExamLabDetailsAliases.PDF_VIEWER_EXAM_DETAILS)
  onUnMountPage(): void {
    this.clearPdfViewerPersistedState();
  }

  private clearPdfViewerPersistedState(): void {
    this.pdfViewer.clearPersistedState();
  }

  @OnMount({ target: 'fetch-exam-lab-details' })
  OnFilterMout(event: MountEvent): void {
    if (event) {
      this.showDetailsInfo(false);
      this.showEstablishmentInfo(false);
      this.filterListParams();
      this.prepareFetch();
    }
  }

  @OnAfterFetch({ target: ['fetch-exam-lab-details'] })
  onAfterExamDetailFetch(event: FetchEvent): void {
    if (event.data) {
      const data = event.data as ExamLabDetail;
      this.showEstablishmentInfo(true);
      this.tabAvailable.setBadge(0);
      this.tabProcessing.setBadge(data.nrStatusProcessing || 0);
      this.showDetailsInfo(true);
      this.getExamsAvailableAndReady();
    }
  }

  @OnMount({ target: 'card-exams-information' })
  onMountCardExamInformation(): void {
    const countAvailable = this.context.get('availableResponseBody') as []
    const countProcessing = this.context.get('processResponseBody') as []
    this.countStatusCardExamDetail(countAvailable?.length || 0, countProcessing?.length || 0);
  }

  @OnMount({ target: 'repeater-exams-processing' })
  onRepeaterExamsProcessing(): void {
    const processingResponseBody = this.context.get('processResponseBody') as [];
    if (processingResponseBody) {
      this.repeaterExamsProcessingApi.setData(processingResponseBody as Data[] || []);
      const countBadgeProcessing = processingResponseBody?.length;
      this.tabProcessing.setBadge(countBadgeProcessing);
    }
  }

  changeStatusDeckReadyBlockResult(data: ExamLabDetail[]): void {
    data.forEach(element => {
      if (element.blockResult === STATUS_RESULT_EXAM.BLOCK) {
        element.status = STATUS_RESULT_EXAM.READY;
      }
    });
  }

  getExamsAvailableAndReady(): void {
    const patientId = this.context.get('patientId');
    const cdMedico = this.context.get('cdMedico');
    const dtProcedimento = this.context.get('dtProcedimento');
    HttpService.get(
      APPLICATION_ENDPOINTS.GET_EXAMS_LAB_LIST, { cdMedico, dtProcedimento, patientId, status: 'A' }
    ).then(responseStatusAvailable => {
      const examsAvailable = responseStatusAvailable.body as ExamLabDetail[] || [];
      this.changeStatusDeckReadyBlockResult(examsAvailable);
      this.context.set({ availableResponseBody: examsAvailable });
      this.onAfterFetchAvailableList(examsAvailable);
      HttpService.get(
        APPLICATION_ENDPOINTS.GET_EXAMS_LAB_LIST, { cdMedico, dtProcedimento, patientId, status: 'R' }
      ).then(responseStatusReady => {
        const examsReady = responseStatusReady.body as ExamLabDetail[] || [];
        this.onAfterFetchReadyList(examsReady);
        this.context.set({ readyResponseBody: examsReady });
        this.deckExamsAvailableAndReadyAPI.setData([...examsReady, ...examsAvailable] as unknown as DeckValue[]);
        this.deckExamsAvailableAndReadyAPI.persistState();
        this.pdfViewer.clearPersistedState();
        HttpService.get(
          APPLICATION_ENDPOINTS.GET_EXAM_PROCESSING_LIST, { cdMedico, dtProcedimento, patientId }
        ).then(responseProcessing => {
          const examsProcessing = responseProcessing.body as ExamLabDetails[] || [];
          this.context.set({ processResponseBody: examsProcessing });
          this.tabProcessing.setBadge(examsProcessing.length || 0);
          this.countStatusCardExamDetail(examsProcessing.length || 0, examsAvailable.length || 0);
        })
      });
    });
  }

  onAfterFetchAvailableList(examList: ExamLabDetail[]): void {
    if (!examList) {
      return;
    }

    const isExamListNotEmpty = examList.length > 0;

    this.checkReviewTag(examList);

    if (isExamListNotEmpty) {
      examList.forEach(exam => {
        HttpService.post(APPLICATION_ENDPOINTS.POST_UPDATE_VIEW_EXAM, {
          ieTipoItem: exam.type,
          nrPrescricao: exam.nrPrescricao,
          nrSequencia: exam.nrSequencia,
          patientId: UserContext.patientId
        });
      });
    }

    if('tab1' === this.tabAvailable.getActiveTab() && isExamListNotEmpty) {
      this.loadReport(examList);
    }

    const currentCount = this.tabAvailable.getBadge() as number || 0;
    this.tabAvailable.setBadge(currentCount + examList.length)
  }

  onAfterFetchReadyList(data: ExamLabDetail[]): void {
    if (data) {
      const examList = data;
      const currentCount = this.tabAvailable.getBadge() as number || 0;
      this.tabAvailable.setBadge(currentCount + examList.length)
    }
  }

  getDeckAvailableAndReadyTitle(status: string): string {
    switch (status) {
      case 'R': return MessageService.translate('patient.exam-lab.deck-ready');
      case 'A': return MessageService.translate('patient.exam-lab.deck-available');
      default: return '';
    }
  }

  @OnDynamicContentRender({ target: 'deck-exams-available' })
  handleDeckAvailableAndReadyHeader(event: RenderEvent): void {
    if (event) {
      const { render } = event;
      const data = event.data as ExamLabDetail[];
      const title = this.getDeckAvailableAndReadyTitle(data[0].status);
      const renderCustomDeckHeader: () => JSX.Element = () => {
        return (
          <div className='c-deck__group-header-default-content'>
            {title}
          </div>
        );
      }
      render(renderCustomDeckHeader);
    }
  }

  @OnClick({ target: ['deck-exams'] })
  onCardDeckClick(event: ClickEvent): void {
    if (event && event.value) {
      this.showDetailsInfo(false);
      this.showEstablishmentInfo(false);
      this.reloadPageConfig();
      this.loadExamPage(event);
    }
  }

  @OnClick({ target: ['download-exam-btn', 'menu-option-download-report'] })
  onDownloadBtnClick(event: ClickEvent): void {
    if (event) {
      this.downloadReport();
    }
  }

  @OnClick({ target: ['button-email'] })
  onClickMailSend(event: ClickEvent): void {
    if (event) {
      const onModalClose = (): void => {
        return;
      };
      const mailParams = {
        emailType: 'report',
        params: this.context.get('reportParams') as Data
      }
      ModalService.openDefault({
        closeable: true,
        contextData: mailParams,
        dockButtons: [],
        fragment: 'send-mail',
        onClose: onModalClose,
        title: 'patient.send-by-email',
      });
    }
  }

  @OnAfterFetch({ target: 'fetch-exams' })
  onAfterFetchExams(event: FetchEvent): void {
    const fetchData = event.data as ExamLabDetail[];

    this.checkReviewTag(fetchData);
    this.setCardOnSidebar(fetchData);
  }

  reloadPageConfig(): void {
    const activeTab = this.tabAvailable.getActiveTab();
    if('tab1' === activeTab) {
      this.pdfViewer.setVisible(false);
    }
    this.tabAvailable.setActiveTab('tab1');
    this.tabProcessing.setActiveTab('tab1');
  }

  loadExamPage(event: ClickEvent): void {
    const { filterFromDate, filterExamStatus, filterExamType } = this.historyService.getCurrentRouteQueryParams() as CurrentRouteParams;
    const { nrSequencia, nrPrescricao, type, dtProcedimento, cdMedico } = event.value;
    const { patientId } = UserContext;
    this.clearPdfViewerPersistedState();
    if (type === 'L') {
      // TODO: Refactor to not use query parameters and don't forget to adjust the "pushRouteByOpenModule" method of the "dashboard" fragment and the "pushToUrl" method of the "patient-root" fragment.
      this.historyService.push(APPLICATION_ROUTES.EXAM_LAB_DETAILS, {}, {
        searchParams: {
          cdMedico: cdMedico as string,
          dtProcedimento: dtProcedimento as string,
          filterExamStatus,
          filterExamType,
          filterFromDate,
          patientId: patientId,
        }
      });

      this.fetchExamDetails.setParameters({
        cdMedico,
        dtProcedimento,
        examType: type,
        patientId,
      });

      this.fetchExamDetails.setEndpoint(APPLICATION_ENDPOINTS.GET_EXAM_DETAILS);
      this.fetchExamDetails.forceFetch();
      this.context.set({
        cdMedico,
        dtProcedimento,
        patientId,
      })
    } else {
      // TODO: Refactor to not use query parameters and don't forget to adjust the "pushRouteByOpenModule" method of the "dashboard" fragment and the "pushToUrl" method of the "patient-root" fragment.
      this.historyService.push(APPLICATION_ROUTES.EXAM_DETAILS, {}, {
        searchParams: {
          dtProcedimento: dtProcedimento as string,
          examType: type as string,
          filterExamStatus,
          filterExamType,
          filterFromDate,
          nrPrescricao: nrPrescricao as string,
          nrSequencia: nrSequencia as string,
          patientId: patientId,
        }
      });
    }
  }

  prepareFetch(): void {
    const { cdMedico, dtProcedimento } = this.historyService.getCurrentRouteQueryParams();
    const { patientId } = UserContext;
    this.context.set({
      cdMedico: cdMedico as Value,
      dtProcedimento: dtProcedimento as Value,
      patientId,
    })
    this.fetchExamDetails.setParameters({
      cdMedico: cdMedico as Value,
      dtProcedimento: dtProcedimento as Value,
      examType: 'L',
      patientId,
    });
    this.fetchExamDetails.setEndpoint(APPLICATION_ENDPOINTS.GET_EXAM_DETAILS);
    this.fetchExamDetails.forceFetch();
  }

  filterListParams(): void {
    const { filterFromDate, filterExamStatus, filterExamType } = this.historyService.getCurrentRouteQueryParams() as CurrentRouteParams;
    const { patientId } = UserContext;
    const filterFromDateVerified = filterFromDate === 'null' ? null : filterFromDate;
    this.fetchExamsApi.setParameters({
      filterExamStatus,
      filterExamType,
      filterFromDateVerified,
      patientId,
    });
    this.fetchExamsApi.forceFetch();
  }

  downloadReport(): void {
    if (this.pdfViewer.getSrc() != null) {
      const reportService = new ReportService(this);
      reportService.openInNewWindow(this.pdfViewer.getSrc());
    }
  }

  loadReport(examList: ExamLabDetail[]): void {
    const listOfSequence: string[] = [];
    const listOfPrescription: string[] = [];
    const listOfSequencePrescription: string[] = [];
    const listOfInternSequenceNumber: string[] = [];
    const { pdfViewer, emailButton, downloadButton } = this;

    if(!examList || examList.length === 0){
      this.showReport(false);
      this.showReportOptions(false, pdfViewer, emailButton, downloadButton);
      this.panelReportLoader.setVisible(false);
    }

    examList.forEach(exam => {
      const { blockResult, nrPrescricao, nrSeqExame, nrSequencia, internSequenceNumber, status } = exam as ExamLabDetail;

      if(status === STATUS_RESULT_EXAM.READY || blockResult === STATUS_RESULT_EXAM.BLOCK) {
        return;
      }

      listOfSequence.push(nrSeqExame);
      listOfPrescription.push(nrPrescricao);
      listOfSequencePrescription.push(nrSequencia);
      listOfInternSequenceNumber.push(internSequenceNumber);
    });

    const params = {
      CD_ESTABELECIMENTO: examList[0].cdEstabelecimento,
      EXAM_NAME: examList[0].nmExame,
      EXAM_TYPE: EXAMS_TYPE.LABORATORY,
      NR_PRESCRICOES: listOfPrescription.toString(),
      NR_SEQ_INTERNOS: listOfInternSequenceNumber.toString(),
      NR_SEQ_PRESCR: listOfSequencePrescription.toString(),
      NR_SEQUENCIAS: listOfSequence.toString(),
      VL_PARAM_STATUS_EXAME: VALUE_PARAMS.STATUS_EXAME,
    }

    this.context.set({ reportParams: params });

    const reportService = new ReportService(this);
    this.panelReportLoader.setVisible(true);
    const { panelReportLoader, panelReportError, showReportOptions } = this;

    reportService.gerarRelatorio(params).then(file => {
      HttpService.get(file, { responseType: 'blob' }).then(response=> {
        const fileURL = URL.createObjectURL(response.body as Blob);
        pdfViewer.setSrc(fileURL);
        panelReportError.setVisible(false);
        pdfViewer.setVisible(true);
        pdfViewer.persistState();
        showReportOptions(true, pdfViewer, emailButton,  downloadButton);
        panelReportLoader.setVisible(false);
      });
    }).catch(() => {
      panelReportError.setVisible(true);
      showReportOptions(false, pdfViewer, emailButton, downloadButton);
      panelReportLoader.setVisible(false);
    });
  }

  showReport(show: boolean): void {
    this.pdfViewer.setVisible(show);
    this.panelReportLoader.setVisible(!show);
  }

  showReportOptions(show: boolean, pdfViewer: PDFViewerAPI, emailButton: ActionAPI, downloadButton: ActionAPI): void {
    pdfViewer.setVisible(show);
    emailButton.setEnabled(show);
    downloadButton.setEnabled(show);
  }

  showDetailsInfo(show: boolean): void {
    this.panelDetailsLoaderOne.setVisible(!show);
    this.panelExamTitle.setVisible(show);
    this.panelDetailsLoaderTwo.setVisible(!show);
    this.panelExamInfo.setVisible(show);
  }

  showEstablishmentInfo(show: boolean): void {
    this.panelEstablishmentLoader.setVisible(!show);
    this.panelEstablishmentInfo.setVisible(show);
  }

  checkReviewTag(examList: ExamLabDetail[]): void {
    examList.forEach(exam => {
      exam.review = exam.ieReview && exam.ieReview === 'S' ? true : false;
    })
  }

  setCardOnSidebar(examList: ExamLabDetail[]): void {
    examList.forEach(exam => {
      exam.cardOnSidebar = true;
    })
  }

  private countStatusCardExamDetail(countAvailable: number, countProcessing: number): void {
    if (countAvailable >= 1 && countProcessing >= 1) {
      this.cardInformationApi.setStatus('I');
    }
  }
}

export default ExamLabDetails;
